import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { api, API_URL } from '../utils/api';
import axios from 'axios';

export default function DiagnosticPage() {
  const [results, setResults] = useState({
    envVarCheck: null,
    apiUrlCheck: null,
    backendHealthCheck: null,
    questionsEndpointCheck: null,
    corsCheck: null
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    runDiagnostics();
  }, []);

  const runDiagnostics = async () => {
    setLoading(true);
    const newResults = { ...results };

    // Test 1: Environment Variable Check
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    if (backendUrl && backendUrl !== 'undefined') {
      newResults.envVarCheck = {
        status: 'success',
        message: `Backend URL is set: ${backendUrl}`,
        details: backendUrl
      };
    } else {
      newResults.envVarCheck = {
        status: 'error',
        message: 'REACT_APP_BACKEND_URL is not defined or invalid',
        details: backendUrl
      };
    }

    // Test 2: API URL Construction
    newResults.apiUrlCheck = {
      status: 'success',
      message: `API URL: ${API_URL}`,
      details: API_URL
    };

    // Test 3: Backend Health Check
    try {
      const healthResponse = await axios.get(`${backendUrl}/api/health`, {
        timeout: 5000,
        withCredentials: false
      });
      newResults.backendHealthCheck = {
        status: 'success',
        message: 'Backend is reachable and healthy',
        details: healthResponse.data
      };
    } catch (error) {
      newResults.backendHealthCheck = {
        status: 'error',
        message: 'Cannot reach backend server',
        details: error.message
      };
    }

    // Test 4: Questions Endpoint Check
    try {
      const questionsResponse = await api.get('/questions/level/1');
      newResults.questionsEndpointCheck = {
        status: 'success',
        message: `Questions endpoint working - ${questionsResponse.data.questions?.length || 0} questions loaded`,
        details: questionsResponse.data.questions?.length
      };
    } catch (error) {
      newResults.questionsEndpointCheck = {
        status: 'error',
        message: 'Questions endpoint failed',
        details: error.response?.data?.detail || error.message
      };
    }

    // Test 5: CORS Check
    try {
      const corsResponse = await axios.get(`${backendUrl}/api/questions/level/1`, {
        withCredentials: true,
        timeout: 5000
      });
      newResults.corsCheck = {
        status: 'success',
        message: 'CORS configuration is correct',
        details: 'Cross-origin requests working'
      };
    } catch (error) {
      if (error.message.includes('CORS') || error.message.includes('Cross-Origin')) {
        newResults.corsCheck = {
          status: 'error',
          message: 'CORS error detected',
          details: error.message
        };
      } else {
        newResults.corsCheck = {
          status: 'warning',
          message: 'CORS check inconclusive',
          details: error.message
        };
      }
    }

    setResults(newResults);
    setLoading(false);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'error':
        return <XCircle className="w-6 h-6 text-red-500" />;
      case 'warning':
        return <AlertCircle className="w-6 h-6 text-yellow-500" />;
      default:
        return <AlertCircle className="w-6 h-6 text-gray-400" />;
    }
  };

  return (
    <div className="min-h-screen p-8 bg-gradient-to-br from-blue-50 to-purple-50">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold mb-8 text-center">System Diagnostics</h1>
        
        <Card className="p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold">Connection Tests</h2>
            <Button onClick={runDiagnostics} disabled={loading}>
              {loading ? 'Running...' : 'Run Again'}
            </Button>
          </div>

          <div className="space-y-4">
            {/* Environment Variable Check */}
            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
              {getStatusIcon(results.envVarCheck?.status)}
              <div className="flex-1">
                <h3 className="font-semibold">Environment Variable Check</h3>
                <p className="text-sm text-gray-600">{results.envVarCheck?.message}</p>
                <p className="text-xs text-gray-500 mt-1">{results.envVarCheck?.details}</p>
              </div>
            </div>

            {/* API URL Check */}
            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
              {getStatusIcon(results.apiUrlCheck?.status)}
              <div className="flex-1">
                <h3 className="font-semibold">API URL Construction</h3>
                <p className="text-sm text-gray-600">{results.apiUrlCheck?.message}</p>
                <p className="text-xs text-gray-500 mt-1">{results.apiUrlCheck?.details}</p>
              </div>
            </div>

            {/* Backend Health Check */}
            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
              {getStatusIcon(results.backendHealthCheck?.status)}
              <div className="flex-1">
                <h3 className="font-semibold">Backend Health Check</h3>
                <p className="text-sm text-gray-600">{results.backendHealthCheck?.message}</p>
                <p className="text-xs text-gray-500 mt-1">{JSON.stringify(results.backendHealthCheck?.details)}</p>
              </div>
            </div>

            {/* Questions Endpoint Check */}
            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
              {getStatusIcon(results.questionsEndpointCheck?.status)}
              <div className="flex-1">
                <h3 className="font-semibold">Questions Endpoint Check</h3>
                <p className="text-sm text-gray-600">{results.questionsEndpointCheck?.message}</p>
                <p className="text-xs text-gray-500 mt-1">{JSON.stringify(results.questionsEndpointCheck?.details)}</p>
              </div>
            </div>

            {/* CORS Check */}
            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
              {getStatusIcon(results.corsCheck?.status)}
              <div className="flex-1">
                <h3 className="font-semibold">CORS Check</h3>
                <p className="text-sm text-gray-600">{results.corsCheck?.message}</p>
                <p className="text-xs text-gray-500 mt-1">{results.corsCheck?.details}</p>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-blue-50">
          <h3 className="font-semibold mb-2">How to Fix Issues:</h3>
          <ul className="list-disc list-inside space-y-2 text-sm">
            <li>If Environment Variable check fails: Update REACT_APP_BACKEND_URL in your deployment settings</li>
            <li>If Backend Health check fails: Verify your backend URL is correct and server is running</li>
            <li>If Questions Endpoint fails: Check backend API is properly configured</li>
            <li>If CORS check fails: Ensure your domain is added to backend CORS_ORIGINS</li>
          </ul>
        </Card>
      </div>
    </div>
  );
}
